// components/keyboard/shortcutsConfig.ts
import { ShortcutConfig } from './types';
import toast from 'react-hot-toast';

// ============================================================================
// FUNCTION REGISTRIES - Store references to page-specific functions
// ============================================================================

let navigateFunction: ((path: string) => void) | null = null;
let openModalFunction: ((modalType: string) => void) | null = null;

// Page-specific function registries
const pageRegistries = {
  // Configuration page
  config: {
    setActiveTab: null as ((tabId: string) => void) | null,
  },
  // Quotations page
  quotations: {
    setActiveTab: null as ((tabId: string) => void) | null,
  },
  // Reports page
  reports: {
    setActiveTab: null as ((tabId: string) => void) | null,
  },
  // Tasks page
  tasks: {
    addNewTaskRow: null as (() => void) | null,
    refreshTasks: null as (() => void) | null,
    selectAllTasks: null as (() => void) | null,
    deleteSelectedTasks: null as (() => void) | null,
    exportTasks: null as (() => void) | null,
    importTasks: null as (() => void) | null,
    saveChanges: null as (() => void) | null,
    endDay: null as (() => void) | null,
    addClient: null as (() => void) | null,
    addStatus: null as (() => void) | null,
    switchToTab: null as ((tabIndex: number) => void) | null,
  },
};

// ============================================================================
// SETTER FUNCTIONS - Called from components to register their functions
// ============================================================================

export const setNavigateFunction = (navigate: (path: string) => void) => {
  navigateFunction = navigate;
 // console.log('✅ Navigate function registered');
};

export const setOpenModalFunction = (openModal: (modalType: string) => void) => {
  openModalFunction = openModal;
 // console.log('✅ Open modal function registered');
};

export const setConfigPageFunctions = (functions: Partial<typeof pageRegistries.config>) => {
  pageRegistries.config = { ...pageRegistries.config, ...functions };
 // console.log('✅ Config page functions registered:', Object.keys(functions));
};

export const setQuotationsPageFunctions = (functions: Partial<typeof pageRegistries.quotations>) => {
  pageRegistries.quotations = { ...pageRegistries.quotations, ...functions };
 // console.log('✅ Quotations page functions registered:', Object.keys(functions));
};

export const setReportsPageFunctions = (functions: Partial<typeof pageRegistries.reports>) => {
  pageRegistries.reports = { ...pageRegistries.reports, ...functions };
 // console.log('✅ Reports page functions registered:', Object.keys(functions));
};

export const setTaskPageFunctions = (functions: Partial<typeof pageRegistries.tasks>) => {
  pageRegistries.tasks = { ...pageRegistries.tasks, ...functions };
 // console.log('✅ Task page functions registered:', Object.keys(functions));
};

// ============================================================================
// HELPER FUNCTIONS
// ============================================================================

const navigateTo = (path: string) => {
  if (navigateFunction) {
    navigateFunction(path);
  } else {
    console.warn('⚠️ Navigate function not registered');
    window.location.href = path;
  }
};

const openModal = (modalType: string) => {
  if (openModalFunction) {
    openModalFunction(modalType);
  } else {
    console.warn('⚠️ Open modal function not registered');
  }
};

// ============================================================================
// SHORTCUT DEFINITIONS
// ============================================================================

// 1. MAIN NAVIGATION SHORTCUTS (Ctrl/Cmd + Alt + 1-6)
export const navigationShortcuts: ShortcutConfig[] = [
  {
    id: 'nav-my-tasks',
    name: 'My Tasks',
    description: 'Navigate to My Tasks page',
    category: 'Navigation',
    keys: {
      windows: ['ctrl+alt+1'],
      macos: ['cmd+option+1'],
    },
    callback: () => {
      navigateTo('/tasks');
      toast.success('Navigated to My Tasks');
    },
    global: true,
  },
  {
    id: 'nav-team-tasks',
    name: 'My Team Tasks',
    description: 'Navigate to My Team Tasks page',
    category: 'Navigation',
    keys: {
      windows: ['ctrl+alt+2'],
      macos: ['cmd+option+2'],
    },
    callback: () => {
      navigateTo('/team-tasks');
      toast.success('Navigated to Team Tasks');
    },
    global: true,
  },
  {
    id: 'nav-all-tasks',
    name: 'All Tasks',
    description: 'Navigate to All Tasks page',
    category: 'Navigation',
    keys: {
      windows: ['ctrl+alt+3'],
      macos: ['cmd+option+3'],
    },
    callback: () => {
      navigateTo('/all-tasks');
      toast.success('Navigated to All Tasks');
    },
    global: true,
  },
  {
    id: 'nav-quotations',
    name: 'Quotations & Invoices',
    description: 'Navigate to Quotations & Invoices page',
    category: 'Navigation',
    keys: {
      windows: ['ctrl+alt+4'],
      macos: ['cmd+option+4'],
    },
    callback: () => {
      navigateTo('/invoicing');
      toast.success('Navigated to Quotations');
    },
    global: true,
  },
  {
    id: 'nav-reports',
    name: 'Reports',
    description: 'Navigate to Reports page',
    category: 'Navigation',
    keys: {
      windows: ['ctrl+alt+5'],
      macos: ['cmd+option+5'],
    },
    callback: () => {
      navigateTo('/reports');
      toast.success('Navigated to Reports');
    },
    global: true,
  },
  {
    id: 'nav-configurations',
    name: 'Configurations',
    description: 'Navigate to Configurations page',
    category: 'Navigation',
    keys: {
      windows: ['ctrl+alt+6'],
      macos: ['cmd+option+6'],
    },
    callback: () => {
      navigateTo('/configurations');
      toast.success('Navigated to Configurations');
    },
    global: true,
  },
];

// 2. CONFIGURATION SUB-TABS (Ctrl/Cmd + Alt + Letter)
export const configurationShortcuts: ShortcutConfig[] = [
  {
    id: 'config-clients',
    name: 'Clients Configuration',
    description: 'Go to Clients configuration',
    category: 'Configuration',
    keys: {
      windows: ['ctrl+alt+c'],
      macos: ['cmd+option+c'],
    },
    callback: () => {
      if (pageRegistries.config.setActiveTab) {
        pageRegistries.config.setActiveTab('clients');
        toast.success('Switched to Clients');
      } else {
        toast.error('Not on Configurations page');
      }
    },
    context: ['configurations'],
  },
  {
    id: 'config-tasks',
    name: 'Tasks Configuration',
    description: 'Go to Tasks configuration',
    category: 'Configuration',
    keys: {
      windows: ['ctrl+alt+t'],
      macos: ['cmd+option+t'],
    },
    callback: () => {
      if (pageRegistries.config.setActiveTab) {
        pageRegistries.config.setActiveTab('tasks');
        toast.success('Switched to Tasks');
      } else {
        toast.error('Not on Configurations page');
      }
    },
    context: ['configurations'],
  },
  {
    id: 'config-subtasks',
    name: 'Sub-tasks Configuration',
    description: 'Go to Sub-tasks configuration',
    category: 'Configuration',
    keys: {
      windows: ['ctrl+alt+s'],
      macos: ['cmd+option+s'],
    },
    callback: () => {
      if (pageRegistries.config.setActiveTab) {
        pageRegistries.config.setActiveTab('sub-tasks');
        toast.success('Switched to Sub-tasks');
      } else {
        toast.error('Not on Configurations page');
      }
    },
    context: ['configurations'],
  },
  {
    id: 'config-invoices',
    name: 'Invoices Configuration',
    description: 'Go to Invoices configuration',
    category: 'Configuration',
    keys: {
      windows: ['ctrl+alt+i'],
      macos: ['cmd+option+i'],
    },
    callback: () => {
      if (pageRegistries.config.setActiveTab) {
        pageRegistries.config.setActiveTab('invoices');
        toast.success('Switched to Invoices');
      } else {
        toast.error('Not on Configurations page');
      }
    },
    context: ['configurations'],
  },
  {
    id: 'config-priority',
    name: 'Priority Configuration',
    description: 'Go to Priority configuration',
    category: 'Configuration',
    keys: {
      windows: ['ctrl+alt+p'],
      macos: ['cmd+option+p'],
    },
    callback: () => {
      if (pageRegistries.config.setActiveTab) {
        pageRegistries.config.setActiveTab('priority');
        toast.success('Switched to Priority');
      } else {
        toast.error('Not on Configurations page');
      }
    },
    context: ['configurations'],
  },
  {
    id: 'config-status',
    name: 'Status Configuration',
    description: 'Go to Status configuration',
    category: 'Configuration',
    keys: {
      windows: ['ctrl+alt+u'],
      macos: ['cmd+option+u'],
    },
    callback: () => {
      if (pageRegistries.config.setActiveTab) {
        pageRegistries.config.setActiveTab('status');
        toast.success('Switched to Status');
      } else {
        toast.error('Not on Configurations page');
      }
    },
    context: ['configurations'],
  },
  {
    id: 'config-users',
    name: 'Users Configuration',
    description: 'Go to Users configuration',
    category: 'Configuration',
    keys: {
      windows: ['ctrl+alt+r'],
      macos: ['cmd+option+r'],
    },
    callback: () => {
      if (pageRegistries.config.setActiveTab) {
        pageRegistries.config.setActiveTab('users');
        toast.success('Switched to Users');
      } else {
        toast.error('Not on Configurations page');
      }
    },
    context: ['configurations'],
  },
  {
    id: 'config-preferences',
    name: 'Preferences Configuration',
    description: 'Go to Preferences configuration',
    category: 'Configuration',
    keys: {
      windows: ['ctrl+alt+e'],
      macos: ['cmd+option+e'],
    },
    callback: () => {
      if (pageRegistries.config.setActiveTab) {
        pageRegistries.config.setActiveTab('preferences');
        toast.success('Switched to Preferences');
      } else {
        toast.error('Not on Configurations page');
      }
    },
    context: ['configurations'],
    global: false,

  },
];

// 3. QUOTATIONS SUB-TABS (Ctrl/Cmd + Option + Letter)
export const quotationsShortcuts: ShortcutConfig[] = [
  {
    id: 'quotations-pending',
    name: 'Pending Invoices',
    description: 'Go to Pending Invoices',
    category: 'Quotations',
    keys: {
      windows: ['ctrl+alt+p'],
      macos: ['cmd+option+p'],
    },
    callback: () => {
      if (pageRegistries.quotations.setActiveTab) {
        pageRegistries.quotations.setActiveTab('pending');
        toast.success('Switched to Pending Invoices');
      } else {
        toast.error('Not on Quotations page');
      }
    },
    context: ['invoicing'],
  },
  {
    id: 'generated',
    name: 'Invoices Generated',
    description: 'Go to Invoices Generated',
    category: 'Quotations',
    keys: {
      windows: ['ctrl+alt+g'],
      macos: ['cmd+option+g'],
    },
    callback: () => {
      if (pageRegistries.quotations.setActiveTab) {
        pageRegistries.quotations.setActiveTab('generated');
        toast.success('Switched to Generated Invoices');
      } else {
        toast.error('Not on Quotations page');
      }
    },
    context: ['invoicing'],
    global: false,
    preventDefault: true,
  },
  {
    id: 'sent',
    name: 'Invoices Sent',
    description: 'Go to Invoices Sent',
    category: 'Quotations',
    keys: {
      windows: ['ctrl+alt+s'],
      macos: ['cmd+option+s'],
    },
    callback: () => {
      if (pageRegistries.quotations.setActiveTab) {
        pageRegistries.quotations.setActiveTab('sent');
        toast.success('Switched to Sent Invoices');
      } else {
        toast.error('Not on Quotations page');
      }
    },
    context: ['invoicing'],
    global: false,
    preventDefault: true,

  },
];

// 4. REPORTS SUB-TABS (Ctrl/Cmd + Option + Letter)
export const reportsShortcuts: ShortcutConfig[] = [
  {
    id: 'day-end',
    name: 'Day End Report',
    description: 'Go to Day End Report',
    category: 'Reports',
    keys: {
      windows: ['ctrl+shift+d'],
      macos: ['cmd+shift+d'],
    },
    callback: () => {
      if (pageRegistries.reports.setActiveTab) {
        pageRegistries.reports.setActiveTab('day-end');
        toast.success('Switched to Day End Report');
      } else {
        toast.error('Not on Reports page');
      }
    },
    context: ['reports'],
    preventDefault: true, // Add this line
    stopPropagation: true, // Add this line
  },
  {
    id: 'clients',
    name: 'Clients Report',
    description: 'Go to Clients Report',
    category: 'Reports',
    keys: {
      windows: ['ctrl+alt+c'],
      macos: ['cmd+option+c'],
    },
    callback: () => {
      if (pageRegistries.reports.setActiveTab) {
        pageRegistries.reports.setActiveTab('clients');
        toast.success('Switched to Clients Report');
      } else {
        toast.error('Not on Reports page');
      }
    },
    context: ['reports'],
  },
  {
    id: 'users',
    name: 'Users Report',
    description: 'Go to Users Report',
    category: 'Reports',
    keys: {
      windows: ['ctrl+alt+u'],
      macos: ['cmd+option+u'],
    },
    callback: () => {
      if (pageRegistries.reports.setActiveTab) {
        pageRegistries.reports.setActiveTab('users');
        toast.success('Switched to Users Report');
      } else {
        toast.error('Not on Reports page');
      }
    },
    context: ['reports'],
  },
  {
    id: 'tasks',
    name: 'Tasks Report',
    description: 'Go to Tasks Report',
    category: 'Reports',
    keys: {
      windows: ['ctrl+alt+t'],
      macos: ['cmd+option+t'],
    },
    callback: () => {
      if (pageRegistries.reports.setActiveTab) {
        pageRegistries.reports.setActiveTab('tasks');
        toast.success('Switched to Tasks Report');
      } else {
        toast.error('Not on Reports page');
      }
    },
    context: ['reports'],
  },
  {
    id: 'sub-tasks',
    name: 'Sub-tasks Report',
    description: 'Go to Sub-tasks Report',
    category: 'Reports',
    keys: {
      windows: ['ctrl+alt+s'],
      macos: ['cmd+option+s'],
    },
    callback: () => {
      if (pageRegistries.reports.setActiveTab) {
        pageRegistries.reports.setActiveTab('sub-tasks');
        toast.success('Switched to Sub-tasks Report');
      } else {
        toast.error('Not on Reports page');
      }
    },
    context: ['reports'],
  },
];

// 5. TASK PAGE SHORTCUTS
export const taskPageShortcuts: ShortcutConfig[] = [
  {
    id: 'task-add-new',
    name: 'Add New Task',
    description: 'Add new task row',
    category: 'Tasks',
    keys: {
      windows: ['ctrl+alt+n'],
      macos: ['cmd+option+n'],
    },
    callback: () => {
      if (pageRegistries.tasks.addNewTaskRow) {
        pageRegistries.tasks.addNewTaskRow();
      } else {
        toast.error('Not on Tasks page');
      }
    },
    context: ['tasks', 'team-tasks', 'all-tasks'],
  },
  {
    id: 'task-refresh',
    name: 'Refresh Tasks',
    description: 'Refresh task list',
    category: 'Tasks',
    keys: {
      windows: ['ctrl+alt+r'],
      macos: ['cmd+option+r'],
    },
    callback: () => {
      if (pageRegistries.tasks.refreshTasks) {
        pageRegistries.tasks.refreshTasks();
      } else {
        toast.error('Not on Tasks page');
      }
    },
    context: ['tasks', 'team-tasks', 'all-tasks'],
  },
  {
    id: 'task-select-all',
    name: 'Select All Tasks',
    description: 'Select all visible tasks',
    category: 'Tasks',
    keys: {
      windows: ['ctrl+alt+a'],
      macos: ['cmd+option+a'],
    },
    callback: () => {
      if (pageRegistries.tasks.selectAllTasks) {
        pageRegistries.tasks.selectAllTasks();
      } else {
        toast.error('Not on Tasks page');
      }
    },
    context: ['tasks', 'team-tasks', 'all-tasks'],
  },
    {
    id: 'task-delete-selected',
    name: 'Delete Selected',
    description: 'Delete selected tasks',
    category: 'Tasks',
    keys: {
      windows: ['delete'],
      macos: ['delete'],
    },
    callback: () => {
      if (pageRegistries.tasks.deleteSelectedTasks) {
        pageRegistries.tasks.deleteSelectedTasks();
      } else {
        toast.error('Not on Tasks page');
      }
    },
    context: ['tasks', 'team-tasks', 'all-tasks'],
  },
  {
    id: 'task-export',
    name: 'Export Tasks',
    description: 'Export tasks to CSV',
    category: 'Tasks',
    keys: {
      windows: ['ctrl+alt+shift+e'],
      macos: ['cmd+option+shift+e'],
    },
    callback: () => {
      if (pageRegistries.tasks.exportTasks) {
        pageRegistries.tasks.exportTasks();
      } else {
        toast.error('Not on Tasks page');
      }
    },
    context: ['tasks', 'team-tasks', 'all-tasks'],
  },
  {
    id: 'task-import',
    name: 'Import Tasks',
    description: 'Import tasks from CSV',
    category: 'Tasks',
    keys: {
      windows: ['ctrl+alt+shift+i'],
      macos: ['cmd+option+shift+i'],
    },
    callback: () => {
      if (pageRegistries.tasks.importTasks) {
        pageRegistries.tasks.importTasks();
      } else {
        toast.error('Not on Tasks page');
      }
    },
    context: ['tasks', 'team-tasks', 'all-tasks'],
  },
  {
    id: 'task-save',
    name: 'Save Changes',
    description: 'Save all pending changes',
    category: 'Tasks',
    keys: {
      windows: ['ctrl+alt+s'],
      macos: ['cmd+option+s'],
    },
    callback: () => {
      if (pageRegistries.tasks.saveChanges) {
        pageRegistries.tasks.saveChanges();
      } else {
        toast.error('Not on Tasks page');
      }
    },
    context: ['tasks', 'team-tasks', 'all-tasks'],
  },
  {
    id: 'task-end-day',
    name: 'End Day',
    description: 'Open end day modal',
    category: 'Tasks',
    keys: {
      windows: ['ctrl+shift+d'],
      macos: ['cmd+shift+d'],
    },
    callback: () => {
      if (pageRegistries.tasks.endDay) {
        pageRegistries.tasks.endDay();
      } else {
        toast.error('Not on Tasks page');
      }
    },
    context: ['tasks', 'team-tasks', 'all-tasks'],
  },
  {
    id: 'task-add-client',
    name: 'Add Client',
    description: 'Open add client modal',
    category: 'Tasks',
    keys: {
      windows: ['ctrl+alt+shift+c'],
      macos: ['cmd+option+shift+c'],
    },
    callback: () => {
      if (pageRegistries.tasks.addClient) {
        pageRegistries.tasks.addClient();
      } else {
        toast.error('Not on Tasks page');
      }
    },
    context: ['tasks', 'team-tasks', 'all-tasks'],
  },
  {
    id: 'task-add-status',
    name: 'Add Status',
    description: 'Open add status modal',
    category: 'Tasks',
    keys: {
      windows: ['ctrl+alt+shift+s'],
      macos: ['cmd+option+shift+s'],
    },
    callback: () => {
      if (pageRegistries.tasks.addStatus) {
        pageRegistries.tasks.addStatus();
      } else {
        toast.error('Not on Tasks page');
      }
    },
    context: ['tasks', 'team-tasks', 'all-tasks'],
  },
  // Task Tab Navigation (Ctrl/Cmd + 1-4)
  {
    id: 'task-tab-1',
    name: 'Switch to Tab 1',
    description: 'Switch to first task tab',
    category: 'Tasks',
    keys: {
      windows: ['ctrl+1'],
      macos: ['cmd+1'],
    },
    callback: () => {
      if (pageRegistries.tasks.switchToTab) {
        pageRegistries.tasks.switchToTab(0);
      } else {
        toast.error('Not on Tasks page');
      }
    },
    context: ['tasks', 'team-tasks', 'all-tasks'],
  },
  {
    id: 'task-tab-2',
    name: 'Switch to Tab 2',
    description: 'Switch to second task tab',
    category: 'Tasks',
    keys: {
      windows: ['ctrl+2'],
      macos: ['cmd+2'],
    },
    callback: () => {
      if (pageRegistries.tasks.switchToTab) {
        pageRegistries.tasks.switchToTab(1);
      } else {
        toast.error('Not on Tasks page');
      }
    },
    context: ['tasks', 'team-tasks', 'all-tasks'],
  },
  {
    id: 'task-tab-3',
    name: 'Switch to Tab 3',
    description: 'Switch to third task tab',
    category: 'Tasks',
    keys: {
      windows: ['ctrl+3'],
      macos: ['cmd+3'],
    },
    callback: () => {
      if (pageRegistries.tasks.switchToTab) {
        pageRegistries.tasks.switchToTab(2);
      } else {
        toast.error('Not on Tasks page');
      }
    },
    context: ['tasks', 'team-tasks', 'all-tasks'],
  },
  {
    id: 'task-tab-4',
    name: 'Switch to Tab 4',
    description: 'Switch to fourth task tab',
    category: 'Tasks',
    keys: {
      windows: ['ctrl+4'],
      macos: ['cmd+4'],
    },
    callback: () => {
      if (pageRegistries.tasks.switchToTab) {
        pageRegistries.tasks.switchToTab(3);
      } else {
        toast.error('Not on Tasks page');
      }
    },
    context: ['tasks', 'team-tasks', 'all-tasks'],
  },
];

// 6. GENERAL SHORTCUTS
export const generalShortcuts: ShortcutConfig[] = [
  {
    id: 'show-help',
    name: 'Show Help',
    description: 'Show keyboard shortcuts help',
    category: 'General',
    keys: {
      windows: ['?'],
      macos: ['?'],
    },
    callback: () => {
      // This is handled by ShortcutProvider
    },
    global: true,
  },
  {
    id: 'close-modal',
    name: 'Close Modal',
    description: 'Close currently open modal',
    category: 'General',
    keys: {
      windows: ['escape'],
      macos: ['escape'],
    },
    callback: () => {
      // This is handled by ShortcutProvider
    },
    global: true,
  },
  {
    id: 'reset-default',
    name: 'Reset to Default',
    description: 'Reset to default settings',
    category: 'General',
    keys: {
      windows: ['ctrl+alt+shift+r'],
      macos: ['cmd+option+shift+r'],
    },
    callback: () => {
     // console.log('🔄 Reset to default triggered');
      window.dispatchEvent(new CustomEvent('reset-default'));
      toast.success('Reset to default settings');
    },
    global: true,
  },
];

// ============================================================================
// EXPORT ALL SHORTCUTS
// ============================================================================

export const allShortcuts: ShortcutConfig[] = [
  ...navigationShortcuts,
  ...configurationShortcuts,
  ...quotationsShortcuts,
  ...reportsShortcuts,
  ...taskPageShortcuts,
  ...generalShortcuts,
];

// Export count for documentation
export const shortcutStats = {
  total: allShortcuts.length,
  navigation: navigationShortcuts.length,
  configuration: configurationShortcuts.length,
  quotations: quotationsShortcuts.length,
  reports: reportsShortcuts.length,
  tasks: taskPageShortcuts.length,
  general: generalShortcuts.length,
};

console.log('📊 Shortcuts loaded:', shortcutStats);